import { useEffect, useState } from 'react';

import { getHttpClient } from '@/shared/api/http-client.js';

function isStaffMediaPath(url: string): boolean {
  return url.includes('/whatsapp-inbox/media/');
}

/**
 * WAHA file URLs are rewritten to a staff route that requires the bearer token.
 * `<img src>` cannot send that header, so those paths are loaded as blob URLs.
 */
export function useWhatsAppInboxMediaObjectUrl(
  url: string | null,
  mimeType: string | null,
): { src: string | null; pending: boolean } {
  const direct = url !== null && url.length > 0 && !isStaffMediaPath(url) ? url : null;
  const [objectUrl, setObjectUrl] = useState<string | null>(direct);
  const [pending, setPending] = useState(url !== null && url.length > 0 && direct === null);

  useEffect(() => {
    if (url === null || url.length === 0) {
      setObjectUrl(null);
      setPending(false);
      return;
    }
    if (!isStaffMediaPath(url)) {
      setObjectUrl(url);
      setPending(false);
      return;
    }

    setPending(true);
    setObjectUrl(null);

    let cancelled = false;
    let created: string | null = null;
    void getHttpClient()
      .getBlob(url)
      .then((blob) => {
        const type =
          blob.type.length > 0 && blob.type !== 'application/octet-stream'
            ? blob.type
            : (mimeType ?? 'application/octet-stream');
        const typed = blob.type === type ? blob : new Blob([blob], { type });
        const next = URL.createObjectURL(typed);
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        created = next;
        setObjectUrl(next);
        setPending(false);
      })
      .catch(() => {
        if (!cancelled) {
          setObjectUrl(null);
          setPending(false);
        }
      });

    return () => {
      cancelled = true;
      if (created !== null) {
        URL.revokeObjectURL(created);
      }
    };
  }, [url, mimeType]);

  return { src: objectUrl, pending };
}
